import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { AlertCircle, Plus, RefreshCw, Wrench } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';

// ============================================================================
// Types
// ============================================================================

const STATUS_META: Record<string, { label: string; cls: string }> = {
  diagnosing:              { label: 'Diagnosing',         cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  awaiting_patch_approval: { label: 'Awaiting patch',     cls: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  applying_patch:          { label: 'Applying patch',     cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  running_canary:          { label: 'Running canary',     cls: 'bg-purple-100 text-purple-800 border-purple-300' },
  passed:                  { label: 'Passed',             cls: 'bg-green-100 text-green-800 border-green-300' },
  needs_next_patch:        { label: 'Needs next patch',   cls: 'bg-orange-100 text-orange-800 border-orange-300' },
  needs_hitl:              { label: 'Needs review',       cls: 'bg-red-100 text-red-800 border-red-300' },
  rolled_back:             { label: 'Rolled back',        cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  failed:                  { label: 'Failed',             cls: 'bg-red-100 text-red-800 border-red-300' },
};

interface SessionSummary {
  id: string;
  site_id: string;
  category_path: string;
  product_type: string | null;
  status: string;
  started_at: string;
  updated_at: string;
}

const BASE = '/api/admin/category-repair';

// ============================================================================
// Fetch helpers
// ============================================================================

async function fetchSessions(siteId?: string): Promise<SessionSummary[]> {
  const qs = siteId ? `?siteId=${encodeURIComponent(siteId)}` : '';
  const res = await authFetch(`${BASE}/sessions${qs}`);
  if (!res.ok) throw new Error(`HTTP ${res.status} from ${BASE}/sessions`);
  return res.json();
}

async function createSession(siteId: string, categoryPath: string, productType: string): Promise<{ session: SessionSummary }> {
  const res = await authFetch(`${BASE}/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId, categoryPath, productType: productType || undefined }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

// ============================================================================
// Status badge
// ============================================================================

function StatusBadge({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? { label: status, cls: 'bg-slate-100 text-slate-700 border-slate-300' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

// ============================================================================
// New Session modal
// ============================================================================

function NewSessionModal({
  open,
  defaultSiteId,
  onClose,
  onCreated,
}: {
  open: boolean;
  defaultSiteId: string;
  onClose: () => void;
  onCreated: (sessionId: string) => void;
}) {
  const [siteId, setSiteId]           = useState(defaultSiteId);
  const [categoryPath, setCategoryPath] = useState('');
  const [productType, setProductType] = useState('');
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState<string | null>(null);

  useEffect(() => { if (open) { setSiteId(defaultSiteId); setCategoryPath(''); setProductType(''); setError(null); } }, [open, defaultSiteId]);

  async function handleCreate() {
    if (!siteId.trim() || !categoryPath.trim()) {
      setError('Site ID and category path are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { session } = await createSession(siteId.trim(), categoryPath.trim(), productType.trim());
      onCreated(session.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Category Repair Session</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Site ID (UUID)</label>
            <Input value={siteId} onChange={e => setSiteId(e.target.value)} placeholder="uuid" disabled={saving} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Category path</label>
            <Input value={categoryPath} onChange={e => setCategoryPath(e.target.value)} placeholder="e.g. Running / Trail Running Shoes" disabled={saving} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Product type (optional)</label>
            <Input value={productType} onChange={e => setProductType(e.target.value)} placeholder="e.g. trail_running_shoe" disabled={saving} />
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleCreate} disabled={saving}>{saving ? 'Creating…' : 'Create session'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Main page
// ============================================================================

export default function CategoryRepairWorkbench() {
  const navigate = useNavigate();
  const { selectedSite } = useSite();

  const [sessions, setSessions]   = useState<SessionSummary[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [newOpen, setNewOpen]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setSessions(await fetchSessions(selectedSite?.id)); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, [selectedSite?.id]);

  useEffect(() => { load(); }, [load]);

  function handleCreated(sessionId: string) {
    setNewOpen(false);
    navigate(`/catalog/repair/${sessionId}`);
  }

  const activeCount   = sessions.filter(s => !['passed', 'rolled_back', 'failed'].includes(s.status)).length;
  const passedCount   = sessions.filter(s => s.status === 'passed').length;

  return (
    <div className="p-6 space-y-6 max-w-screen-xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wrench className="h-6 w-6" /> Category Repair
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Diagnose broken categories, apply patches, run 5-product canary, accept or rollback.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setNewOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" /> New Session
          </Button>
        </div>
      </div>

      {sessions.length > 0 && (
        <div className="flex gap-4 text-sm text-muted-foreground">
          <span>Active: <span className="font-semibold text-foreground">{activeCount}</span></span>
          <span>Passed: <span className="font-semibold text-green-700">{passedCount}</span></span>
          <span>Total: <span className="font-semibold text-foreground">{sessions.length}</span></span>
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      )}

      {!loading && !error && sessions.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <Wrench className="h-8 w-8 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No repair sessions yet.</p>
            <p className="text-sm mt-1">Start one by clicking "New Session" above.</p>
          </CardContent>
        </Card>
      )}

      {!loading && sessions.length > 0 && (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead>Product type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions.map(s => (
                <TableRow
                  key={s.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/catalog/repair/${s.id}`)}
                >
                  <TableCell className="font-medium max-w-[300px] truncate" title={s.category_path}>
                    {s.category_path}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{s.product_type ?? '—'}</TableCell>
                  <TableCell><StatusBadge status={s.status} /></TableCell>
                  <TableCell className="text-sm text-muted-foreground tabular-nums">
                    {new Date(s.updated_at).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <NewSessionModal
        open={newOpen}
        defaultSiteId={selectedSite?.id ?? ''}
        onClose={() => setNewOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}
